'use client'

import { useEffect, useRef, useState } from 'react'
import { AlertCircle, Volume2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { playAudio } from '@/lib/pronunciation/audio-player'

type PronunciationAudioButtonProps = {
  audioPath?: string
  label: string
  className?: string
  compact?: boolean
  autoPlayKey?: string | number
  children?:
    | React.ReactNode
    | ((status: 'idle' | 'playing' | 'error') => React.ReactNode)
}

export function PronunciationAudioButton({
  audioPath,
  label,
  className,
  compact = false,
  autoPlayKey,
  children
}: PronunciationAudioButtonProps) {
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [status, setStatus] = useState<'idle' | 'playing' | 'error'>(
    audioPath ? 'idle' : 'error'
  )

  useEffect(() => {
    return () => {
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current)
    }
  }, [])

  useEffect(() => {
    if (autoPlayKey === undefined || !audioPath) return
    void play()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPlayKey, audioPath])

  const play = () => {
    if (!audioPath) return

    if (resetTimerRef.current) clearTimeout(resetTimerRef.current)

    const startTime = Date.now()

    playAudio(audioPath, {
      onStart: () => {
        setStatus('playing')
      },
      onEnded: () => {
        // Enforce a minimum 400ms visual 'playing' state to prevent flickering
        const elapsed = Date.now() - startTime
        const remaining = Math.max(0, 400 - elapsed)
        resetTimerRef.current = setTimeout(() => {
          setStatus((current) => (current === 'playing' ? 'idle' : current))
        }, remaining)
      },
      onError: () => {
        setStatus('error')
      },
      onStop: () => {
        setStatus('idle')
        if (resetTimerRef.current) clearTimeout(resetTimerRef.current)
      }
    })
  }

  const Icon = status === 'error' ? AlertCircle : Volume2

  if (children) {
    const content = typeof children === 'function' ? children(status) : children
    return (
      <button
        type="button"
        onClick={play}
        disabled={!audioPath}
        className={cn(
          'transition-all duration-200 outline-none disabled:cursor-not-allowed',
          className
        )}
        aria-label={
          audioPath ? `Play ${label}` : `Audio unavailable for ${label}`
        }
        title={audioPath ? `Play ${label}` : `Audio unavailable for ${label}`}
      >
        {content}
      </button>
    )
  }

  return (
    <Button
      type="button"
      variant={compact ? 'ghost' : 'outline'}
      size={compact ? 'icon-sm' : 'sm'}
      className={cn(
        'rounded-full transition-[background-color,color,box-shadow,transform]',
        status === 'playing' &&
          'bg-primary text-primary-foreground ring-primary/25 shadow-sm ring-2',
        status === 'playing' && compact && 'scale-105',
        className
      )}
      onClick={play}
      disabled={!audioPath}
      aria-label={
        audioPath ? `Play ${label}` : `Audio unavailable for ${label}`
      }
      title={audioPath ? `Play ${label}` : `Audio unavailable for ${label}`}
    >
      <Icon className="h-4 w-4" />
      {!compact && <span>{status === 'error' ? 'Missing audio' : 'Play'}</span>}
    </Button>
  )
}
